// ItemPerformance.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import "./ItemPerformance.css";
import { translations } from "../../../../utils/lang";
import { Context } from "../../../../context/Context";
import { fetchSaledItemsAPI } from "../../../../utils/api/operations";
import Select from "../../../ui/Select";

export default function ItemPerformance({ storeId, appName, menuCount, to }) {
  const { lang } = useContext(Context);
  const [soldItems, setSoldItems] = useState({ topFive: [], leastFive: [] });
  const [loadFailed, setLoadFailed] = useState(false);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();

  const YEARS = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        value: currentYear - i,
        label: String(currentYear - i),
      })),
    [currentYear],
  );

  const MONTHS = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(2000, i, 1).toLocaleString(
          lang === "ar" ? "ar-SA" : "en-US",
          { month: "long" },
        ),
      })),
    [lang],
  );

  const PERIODS = useMemo(
    () => [
      { value: 1, label: "25->9" },
      { value: 2, label: "10->24" },
    ],
    [],
  );

  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();

  const defaultPeriod = currentDay <= 10 ? 1 : 2;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);

  useEffect(() => {
    if (!to) return;

    const To = new Date(to);
    if (Number.isNaN(To.getTime())) return;

    const month = To.getMonth() + 1;
    const year = To.getFullYear();

    setSelectedMonth(month);
    setSelectedYear(year);

    const day = To.getDate();
    setSelectedPeriod(day <= 10 || day >= 25 ? 1 : 2);
  }, [to]);

  const token = localStorage.getItem("auth-token");
  const showAppName = String(appName || "").toLowerCase() === "lud";

  useEffect(() => {
    if (!token) return undefined;
    // Ignore responses for a period/month/year that is no longer selected.
    let active = true;
    fetchSaledItemsAPI(token, {
      storeId,
      appName,
      period: selectedPeriod,
      year: selectedYear,
      month: selectedMonth,
    }).then((data) => {
      if (!active) return;
      // The helper resolves to undefined when the request failed.
      setLoadFailed(!data);
      setSoldItems(data || { topFive: [], leastFive: [] });
    });
    return () => {
      active = false;
    };
  }, [token, storeId, appName, selectedPeriod, selectedMonth, selectedYear]);

  return (
    <div className="item-performance-card">
      <div>
        <div className="item-performance-card-row">
          <Select
            placeholder="Select period..."
            options={PERIODS}
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
          />

          <Select
            placeholder="Select month..."
            options={MONTHS}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          />

          <Select
            placeholder="Select Year..."
            options={YEARS}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          />
        </div>

        {loadFailed && (
          <p className="monthly-operation-error" role="status">
            {lang === "ar"
              ? "تعذر تحميل أداء الأصناف"
              : "Item performance could not be loaded"}
          </p>
        )}

        <div className="item-performance-grid">
          <div className="item-performance-col">
            <div className="item-performance-title">
              {translations[lang].Operation.topSelling}
            </div>

            {(Array.isArray(soldItems?.topFive) ? soldItems.topFive : []).map((sold, i) => (
              <div
                key={i}
                className={`item-performance-row${showAppName ? " with-app" : ""}`}
              >
                <span className="item-name">
                  {sold?.item?.en || sold?.item?.ar || "—"}
                </span>
                {showAppName && (
                  <span className="app-name">{sold?.app || "-"}</span>
                )}
                <span className="count">
                  {Number.isFinite(sold?.count) ? sold.count : "—"}
                </span>
              </div>
            ))}
          </div>

          <div className="item-performance-col">
            <div className="item-performance-title">
              {translations[lang].Operation.leastSelling}
            </div>

            {(Array.isArray(soldItems?.leastFive) ? soldItems.leastFive : []).map((sold, i) => (
              <div
                key={i}
                className={`item-performance-row${showAppName ? " with-app" : ""}`}
              >
                <span className="item-name">
                  {sold?.item?.en || sold?.item?.ar || "—"}
                </span>
                {showAppName && (
                  <span className="app-name">{sold?.app || "-"}</span>
                )}
                <span className="count">
                  {Number.isFinite(sold?.count) ? sold.count : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="item-performance-footer">
        <div>
          {translations[lang].Operation.items}:{" "}
          <strong>{Number.isFinite(menuCount?.itemCount) ? menuCount.itemCount : "—"}</strong>
        </div>
        <div>
          {translations[lang].Operation.boxes}:{" "}
          <strong>{Number.isFinite(menuCount?.boxCount) ? menuCount.boxCount : "—"}</strong>
        </div>
      </div>
    </div>
  );
}
